import { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { IoMdArrowBack } from "react-icons/io";
import { AuthContext } from '../../Context/AuthContext';
import TenantContext from '../../Context/TenantContext';
import axios from 'axios';

const QuestionsAndAnswers = () => {
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assessmentTimestamp, setAssessmentTimestamp] = useState('');
  const [assessmentType, setAssessmentType] = useState('');
  const [assessmentStatus, setAssessmentStatus] = useState('');
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useContext(AuthContext);
  const { tenant } = useContext(TenantContext) || {};

  useEffect(() => {
    let cancelled = false;
    const fetchAssessment = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('[QuestionsAndAnswers] Starting fetch...');
        console.log('[QuestionsAndAnswers] isAuthenticated:', isAuthenticated);
        console.log('[QuestionsAndAnswers] token:', token ? 'present' : 'missing');
        
        if (!isAuthenticated || !token) {
          console.log('[QuestionsAndAnswers] Not authenticated, aborting');
          setError('Please log in to view assessment details');
          setLoading(false);
          return;
        }
        
        console.log('[QuestionsAndAnswers] Fetching assessment ID:', id);
        console.log('[QuestionsAndAnswers] Tenant:', tenant?.id);
        
        const url =
          import.meta.env.VITE_APP_BASE_API +
          import.meta.env.VITE_APP_API_VERSION +
          "/user/patient/nurse-chat/answers";
        
        console.log('[QuestionsAndAnswers] API URL:', url);
          
        const response = await axios.get(url, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000 // 10 second timeout
        });
        
        console.log('[QuestionsAndAnswers] Response status:', response.status);
        console.log('[QuestionsAndAnswers] Response data:', response.data);
        
        const list = Array.isArray(response.data?.assessments) ? response.data.assessments : [];
        console.log('[QuestionsAndAnswers] Total assessments:', list.length);
        console.log('[QuestionsAndAnswers] Looking for ID:', id);
        console.log('[QuestionsAndAnswers] Available IDs:', list.map(a => a._id));
        
        const current = list.find((a) => String(a?._id) === String(id));
        
        if (!current) {
          if (!cancelled) {
            console.log('[QuestionsAndAnswers] Assessment not found for ID:', id);
            setError('Assessment not found. It may have been deleted or you may not have access to it.');
            setQuestionsAndAnswers([]);
            setAssessmentTimestamp('');
            setAssessmentType('');
          }
          return;
        }
        
        console.log('[QuestionsAndAnswers] Found assessment:', current.name);
        console.log('[QuestionsAndAnswers] Questions count:', current.data?.length);
        console.log('[QuestionsAndAnswers] Sample question:', current.data?.[0]);
        
        if (!cancelled) {
          setQuestionsAndAnswers(current.data || []);
          setAssessmentTimestamp(String(current.timestamp || current.createdAt || ''));
          setAssessmentType(
            current.name || current.assessment_name || current.assessment_type || current.type || 'Assessment'
          );
          setAssessmentStatus(current.status || 'completed');
          console.log('[QuestionsAndAnswers] State updated successfully');
        }
      } catch (e) {
        console.error('[QuestionsAndAnswers] Error:', e);
        console.error('[QuestionsAndAnswers] Error message:', e.message);
        console.error('[QuestionsAndAnswers] Error response:', e.response?.data);
        if (!cancelled) {
          const errorMsg = e.code === 'ECONNABORTED' 
            ? 'Request timeout - server took too long to respond'
            : e.response?.data?.message || e.message || 'Failed to load assessment details';
          setError(errorMsg);
        }
      } finally {
        if (!cancelled) {
          console.log('[QuestionsAndAnswers] Setting loading to false');
          setLoading(false);
        }
      }
    };

    fetchAssessment();
    return () => {
      cancelled = true;
    };
  }, [id, isAuthenticated, token, tenant?.id]);

  const convertToHumanReadable = (dateString) => {
    if (!dateString) return 'N/A';
    console.log("dateString:", dateString)

    const raw = String(dateString).trim();

    const formatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };

    const formatOut = (d) => d.toLocaleString('en-US', formatOptions);

    // 1) ISO 8601 or generally parseable date strings
  if (/[T/-]/.test(raw)) {
      const d = new Date(raw);
      if (!isNaN(d.getTime())) return formatOut(d);
    }

    // 2) Pure numeric compact formats: YYYYMMDDHHmm or YYYYMMDDHHmmss
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 12 || digits.length === 14) {
      const year = parseInt(digits.slice(0, 4), 10);
      const month = parseInt(digits.slice(4, 6), 10) - 1; // 0-indexed
      const day = parseInt(digits.slice(6, 8), 10);
      const hours = parseInt(digits.slice(8, 10), 10);
      const minutes = parseInt(digits.slice(10, 12), 10);
      const seconds = digits.length === 14 ? parseInt(digits.slice(12, 14), 10) : 0;

      const d = new Date(year, month, day, hours, minutes, seconds);
      if (!isNaN(d.getTime())) return formatOut(d);
    }

    // 3) Fallback attempt
    const fallback = new Date(raw);
    if (!isNaN(fallback.getTime())) return formatOut(fallback);

    return 'Invalid Date';
  };

  // Format assessment type for display
  const formatAssessmentType = (type) => {
    if (!type) return 'Assessment';
    const typeMap = {
      'knee-arthroplasty': 'Knee Arthroplasty',
      'hip-arthroplasty': 'Hip Arthroplasty',
      'general-surgery': 'Patient Intake',
      'viral-fever': 'Viral Fever',
      'lscs-pregnancy': 'LSCS Pregnancy',
    };
    return typeMap[type] || type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="mb-6">
          <button 
            onClick={() => navigate('/dashboard/nurse-chat/answers')}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium transition-colors mb-4"
          >
            <IoMdArrowBack className="text-xl" />
            <span>Back to Assessments</span>
          </button>
          
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {formatAssessmentType(assessmentType)}
                </h1>
                <p className="text-gray-600">
                  {assessmentTimestamp ? convertToHumanReadable(assessmentTimestamp) : 'Date not available'}
                </p>
              </div>
              <div className="hidden md:block">
                <div className="bg-blue-100 rounded-full p-4">
                  <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Patient Info */}
        {user && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-lg p-6 mb-6 text-white">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Patient Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white bg-opacity-20 rounded-lg p-3 backdrop-blur-sm">
                <p className="text-blue-100 text-sm mb-1">Name</p>
                <p className="font-medium">
                  {user?.firstName && user?.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : user?.name || 'N/A'
                  }
                </p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-3 backdrop-blur-sm">
                <p className="text-blue-100 text-sm mb-1">Email</p>
                <p className="font-medium">{user?.email || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Loading assessment details...</p>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl shadow-lg p-8 text-center">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Assessment</h3>
            <p className="text-gray-600">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && questionsAndAnswers.length === 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <svg className="w-20 h-20 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Questions Found</h3>
            <p className="text-gray-600">This assessment does not contain any questions or answers.</p>
          </div>
        )}

        {/* Questions and Answers */}
        {!loading && !error && questionsAndAnswers.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Assessment Questions ({questionsAndAnswers.length})
              </h2>
            </div>
            
            {questionsAndAnswers.map((qa, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100"
              >
                <div className="p-6">
                  {/* Question */}
                  <div className="flex gap-4 mb-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700">
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-start gap-2 mb-2">
                        <svg className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-blue-600 uppercase tracking-wide">Question</span>
                      </div>
                      <p className="text-gray-900 text-lg leading-relaxed">{qa.question}</p>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-200 my-4"></div>

                  {/* Answer */}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-start gap-2 mb-2">
                        <svg className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-green-600 uppercase tracking-wide">Answer</span>
                      </div>
                      <p className="text-gray-900 text-lg leading-relaxed font-medium">{qa.answer || 'No answer provided'}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionsAndAnswers;
